import type { Game } from '../core/game';
import type { MasterDie } from '../core/types';
import { dieLevelOf, isCorruptedDie } from './abyssApi';
import { forgeApplyOf, forgeCheckOf, forgePriceOf, forgeRecipesOf, isForgeableOf, isGrandfatherDie } from './forgeApi';
import { formatGold } from './format';
import { callGame, hasMethod } from './gameApi';
import { toast } from './toast';

/**
 * Actions rapides d'une carte de dé (v0.57.0, A122-8 et A122-9) : l'**étoile**
 * des favoris et le bouton **+** du Trempage direct. Le cœur (`setFavorite`,
 * `MasterDie.favorite`) est lu **structurellement** : tant qu'il n'est pas
 * fusionné, l'étoile pose le champ directement sur le dé (repli), la carte reste
 * utilisable.
 */

/** Un favori ? (`MasterDie.favorite` du cœur A122). */
export function isFavorite(die: MasterDie): boolean {
  return (die as { favorite?: boolean }).favorite === true;
}

/** Pose ou retire l'étoile (`game.setFavorite`) ; repli : le champ posé sur le dé. */
export function setFavoriteOf(game: Game, dieId: string, on: boolean): void {
  if (hasMethod(game, 'setFavorite')) {
    callGame(game, 'setFavorite', dieId, on);
    return;
  }
  const die = game.state.inventory.find((d) => d.id === dieId) as ({ favorite?: boolean } & MasterDie) | undefined;
  if (!die) return;
  if (on) die.favorite = true;
  else delete die.favorite;
}

/** Favoris d'abord (ordre relatif conservé) — listes à plat (Équiper, cibles de la Forge). */
export function favoritesFirst<T extends MasterDie>(dice: readonly T[]): T[] {
  return [...dice.filter(isFavorite), ...dice.filter((d) => !isFavorite(d))];
}

/** Sépare les favoris du reste (ordre conservé) — volets « Favoris » et par rareté. */
export function splitFavorites<T extends MasterDie>(dice: readonly T[]): { favorites: T[]; rest: T[] } {
  return { favorites: dice.filter(isFavorite), rest: dice.filter((d) => !isFavorite(d)) };
}

/** Bouton étoile (cible tactile ≥ 40 px par la feuille de style). */
export function buildFavoriteButton(die: MasterDie, game: Game, refresh: () => void): HTMLButtonElement {
  const on = isFavorite(die);
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'die-fav';
  btn.setAttribute('data-action', 'favorite');
  btn.setAttribute('aria-pressed', String(on));
  btn.setAttribute('aria-label', on ? `Retirer ${die.name} des favoris` : `Mettre ${die.name} en favori`);
  btn.title = on ? 'Retirer des favoris' : 'Mettre en favori';
  btn.textContent = on ? '★' : '☆';
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    setFavoriteOf(game, die.id, !on);
    refresh();
  });
  return btn;
}

/** État du Trempage direct d'un dé : prix et niveau, ou `null` (aucun bouton). */
export function temperQuickOf(game: Game, die: MasterDie): { price: number; level: number; affordable: boolean; ok: boolean } | null {
  // Mêmes règles que la Forge : dé Corrupted, pas le Dé de l'aïeul, recette débloquée.
  if (!isCorruptedDie(die) || isGrandfatherDie(die) || !isForgeableOf(game, die).ok) return null;
  const recipe = forgeRecipesOf(game).find((r) => r.id === 'temper');
  if (!recipe || !recipe.unlocked) return null;
  const price = forgePriceOf(game, 'temper', die.id);
  if (price === null) return null;
  const affordable = game.state.gold >= price;
  const ok = affordable && forgeCheckOf(game, { recipe: 'temper', dieId: die.id }).ok;
  return { price, level: dieLevelOf(die), affordable, ok };
}

/** Bouton « + » (Trempage direct) ; `null` quand la recette n'est pas ouverte à ce dé. */
export function buildTemperQuickButton(die: MasterDie, game: Game, refresh: () => void): HTMLButtonElement | null {
  const st = temperQuickOf(game, die);
  if (!st) return null;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'f-btn f-btn--sm die-temper';
  btn.setAttribute('data-action', 'temper-quick');
  btn.dataset.price = String(st.price);
  btn.disabled = !st.ok;
  btn.setAttribute('aria-label', `Tremper ${die.name} : niveau ${st.level} vers ${st.level + 1}, ${formatGold(st.price)} or`);
  btn.title = `Trempage : niveau ${st.level + 1}`;
  btn.innerHTML = `<span class="die-temper-plus" aria-hidden="true">+</span><span class="die-temper-price num">${formatGold(st.price)} or</span>`;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (btn.disabled) return;
    const out = forgeApplyOf(game, { recipe: 'temper', dieId: die.id });
    if (!out) toast('Le Trempage a échoué.', 2400, 'lock');
    refresh();
  });
  return btn;
}

export interface QuickOptions {
  favorite?: boolean;
  temper?: boolean;
}

/**
 * Pose les actions rapides sur une carte construite par `dieCardInnerHtml` :
 * l'étoile à droite du nom, le « + » en bas de la colonne de texte.
 */
export function decorateDieCard(card: HTMLElement, die: MasterDie, game: Game, refresh: () => void, opts: QuickOptions = {}): void {
  card.classList.toggle('die-card--favorite', isFavorite(die));
  if (opts.favorite !== false) card.querySelector('.die-card-head')?.appendChild(buildFavoriteButton(die, game, refresh));
  if (opts.temper) {
    const btn = buildTemperQuickButton(die, game, refresh);
    if (btn) card.querySelector('.die-card-main')?.appendChild(btn);
  }
}
